import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Table, Modal, Badge, Spinner, Alert } from 'react-bootstrap';
import { FaPlus, FaEdit, FaTrash, FaSignOutAlt, FaImage, FaCheckCircle } from 'react-icons/fa';
import axios from 'axios';
import Swal from 'sweetalert2';
import API_URL from '../config/api';

const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  const [trabajos, setTrabajos] = useState([]);
  const [loadingTrabajos, setLoadingTrabajos] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [editingTrabajo, setEditingTrabajo] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    titulo: '',
    descripcion: '',
    categoria: 'Stands',
    cliente: '',
    ubicacion: '',
    tamaño: '',
    materiales: '',
    fecha: '',
    imagenes: []
  });

  const [dragActive, setDragActive] = useState(false);
  const [imagePreviews, setImagePreviews] = useState([]);

  const [activeTab, setActiveTab] = useState('trabajos');
  const [contactos, setContactos] = useState([]);
  const [loadingContactos, setLoadingContactos] = useState(false);

  // Verificar autenticación al cargar
  useEffect(() => {
    const authStatus = sessionStorage.getItem('exhibirte_admin_auth');
    if (authStatus === 'true') {
      setIsAuthenticated(true);
    }
    setIsLoading(false);
  }, []);

  // Cargar datos cuando está autenticado
  useEffect(() => {
    if (isAuthenticated) {
      if (activeTab === 'trabajos') {
        fetchTrabajos();
      } else if (activeTab === 'contactos') {
        fetchContactos();
      }
    }
  }, [isAuthenticated, activeTab]);

  const fetchTrabajos = async () => {
    setLoadingTrabajos(true);
    try {
      const response = await axios.get(`${API_URL}/trabajos`);
      setTrabajos(response.data);
    } catch (error) {
      console.error('Error fetching trabajos:', error);
      Swal.fire('Error', 'No se pudieron cargar los trabajos', 'error');
    }
    setLoadingTrabajos(false);
  };

  const fetchContactos = async () => {
    setLoadingContactos(true);
    try {
      const response = await axios.get(`${API_URL}/contactos`);
      setContactos(response.data);
    } catch (error) {
      console.error('Error fetching contactos:', error);
      Swal.fire('Error', 'No se pudieron cargar los contactos', 'error');
    }
    setLoadingContactos(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');

    try {
      const response = await axios.post(`${API_URL}/admin/validate`, { password });
      if (response.data.valid) {
        sessionStorage.setItem('exhibirte_admin_auth', 'true');
        setIsAuthenticated(true);
      }
    } catch (error) {
      setLoginError('Contraseña incorrecta');
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('exhibirte_admin_auth');
    setIsAuthenticated(false);
  };

  // ... (rest of handler functions for Trabajos) ...
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  const handleFiles = (files) => {
    const fileList = Array.from(files);

    if (fileList.length > 4) {
      Swal.fire('Error', 'Máximo 4 imágenes permitidas', 'error');
      return;
    }

    const validFiles = [];
    const previews = [];

    for (const file of fileList) {
      if (file.size > 800 * 1024) {
        Swal.fire('Error', `La imagen ${file.name} excede el tamaño máximo de 800kb`, 'error');
        return;
      }
      validFiles.push(file);
      previews.push(URL.createObjectURL(file));
    }

    setFormData(prev => ({ ...prev, imagenes: validFiles }));
    setImagePreviews(previews);
  };

  const resetForm = () => {
    setFormData({
      nombre: '',
      titulo: '',
      descripcion: '',
      categoria: 'Stands',
      cliente: '',
      ubicacion: '',
      tamaño: '',
      materiales: '',
      fecha: '',
      imagenes: []
    });
    setImagePreviews([]);
    setEditingTrabajo(null);
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (trabajo) => {
    setEditingTrabajo(trabajo);
    setFormData({
      nombre: trabajo.nombre || '',
      titulo: trabajo.titulo || '',
      descripcion: trabajo.descripcion || '',
      categoria: trabajo.categoria || 'Stands',
      cliente: trabajo.cliente || '',
      ubicacion: trabajo.ubicacion || '',
      tamaño: trabajo.tamaño || '',
      materiales: trabajo.materiales || '',
      fecha: trabajo.fecha ? trabajo.fecha.split('T')[0] : '',
      imagenes: []
    });

    if (trabajo.imagenes && trabajo.imagenes.length > 0) {
      setImagePreviews(trabajo.imagenes.map(img => img.startsWith('http') ? img : `${API_URL}/${img}`));
    } else if (trabajo.imagen) {
      setImagePreviews([trabajo.imagen.startsWith('http') ? trabajo.imagen : `${API_URL}/${trabajo.imagen}`]);
    } else {
      setImagePreviews([]);
    }

    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const data = new FormData();
    Object.keys(formData).forEach(key => {
      if (formData[key] !== null && formData[key] !== '' && key !== 'imagenes') {
        data.append(key, formData[key]);
      }
    });

    if (formData.imagenes && formData.imagenes.length > 0) {
      formData.imagenes.forEach(file => {
        data.append('imagenes', file);
      });
    }

    try {
      if (editingTrabajo) {
        await axios.put(`${API_URL}/trabajos/${editingTrabajo._id}`, data, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        Swal.fire('Éxito', 'Trabajo actualizado correctamente', 'success');
      } else {
        await axios.post(`${API_URL}/trabajos`, data, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        Swal.fire('Éxito', 'Trabajo creado correctamente', 'success');
      }

      setShowModal(false);
      resetForm();
      fetchTrabajos();
    } catch (error) {
      console.error('Error saving trabajo:', error);
      Swal.fire('Error', 'No se pudo guardar el trabajo', 'error');
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: '¿Estás seguro?',
      text: 'Esta acción no se puede deshacer',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`${API_URL}/trabajos/${id}`);
        Swal.fire('Eliminado', 'El trabajo ha sido eliminado', 'success');
        fetchTrabajos();
      } catch (error) {
        console.error('Error deleting trabajo:', error);
        Swal.fire('Error', 'No se pudo eliminar el trabajo', 'error');
      }
    }
  };

  const handleDeleteContacto = async (id) => {
    const result = await Swal.fire({
      title: '¿Estás seguro?',
      text: 'Esta acción no se puede deshacer',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`${API_URL}/contactos/${id}`);
        Swal.fire('Eliminado', 'El mensaje ha sido eliminado', 'success');
        fetchContactos();
      } catch (error) {
        console.error('Error deleting contacto:', error);
        Swal.fire('Error', 'No se pudo eliminar el mensaje', 'error');
      }
    }
  };

  const handleMarkAsRead = async (id) => {
    try {
      await axios.patch(`${API_URL}/contactos/${id}/leido`);
      fetchContactos();
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  // Loading screen
  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh', background: '#f8f9fa' }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  // Login screen
  if (!isAuthenticated) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1E5096 0%, #163D73 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Container>
          <Row className="justify-content-center">
            <Col md={5}>
              <Card className="shadow-lg border-0">
                <Card.Body className="p-5">
                  <div className="text-center mb-4">
                    <img src="/LogoExhibirte.png" alt="Exhibirte" style={{ height: '60px', marginBottom: '20px' }} />
                    <h3 style={{ color: '#1E5096', fontWeight: 'bold' }}>Panel de Administración</h3>
                    <p className="text-muted">Ingresa tu contraseña para continuar</p>
                  </div>

                  <Form onSubmit={handleLogin}>
                    <Form.Group className="mb-4">
                      <Form.Label>Contraseña</Form.Label>
                      <Form.Control
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Ingresa la contraseña"
                        required
                        size="lg"
                      />
                    </Form.Group>

                    {loginError && (
                      <Alert variant="danger" className="mb-3">
                        {loginError}
                      </Alert>
                    )}

                    <Button
                      type="submit"
                      variant="primary"
                      size="lg"
                      className="w-100"
                      style={{ backgroundColor: '#1E5096', borderColor: '#1E5096' }}
                    >
                      Ingresar
                    </Button>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  // Dashboard
  return (
    <div style={{ minHeight: '100vh', background: '#f8f9fa' }}>
      {/* Header */}
      <nav className="navbar navbar-dark mb-4" style={{ backgroundColor: '#1E5096' }}>
        <Container fluid>
          <span className="navbar-brand d-flex align-items-center">
            <img src="/LogoExhibirte.png" alt="Exhibirte" style={{ height: '35px', marginRight: '10px' }} />
            <strong>ADMIN PANEL</strong>
          </span>
          <Button variant="outline-light" size="sm" onClick={handleLogout}>
            <FaSignOutAlt className="me-2" />
            Cerrar Sesión
          </Button>
        </Container>
      </nav>

      <Container className="py-4">
        {/* Navigation Tabs */}
        <div className="d-flex mb-4 gap-2">
          <Button
            variant={activeTab === 'trabajos' ? 'primary' : 'light'}
            onClick={() => setActiveTab('trabajos')}
            style={activeTab === 'trabajos' ? { backgroundColor: '#1E5096', borderColor: '#1E5096' } : {}}
          >
            <FaImage className="me-2" /> Trabajos
          </Button>
          <Button
            variant={activeTab === 'contactos' ? 'primary' : 'light'}
            onClick={() => setActiveTab('contactos')}
            style={activeTab === 'contactos' ? { backgroundColor: '#1E5096', borderColor: '#1E5096' } : {}}
          >
            <FaEdit className="me-2" /> Mensajes de Contacto
            {contactos.filter(c => !c.leido).length > 0 && (
              <Badge bg="danger" className="ms-2">{contactos.filter(c => !c.leido).length}</Badge>
            )}
          </Button>
        </div>

        {activeTab === 'contactos' && (
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white border-0 py-3">
              <h5 className="mb-0" style={{ color: '#1E5096' }}>Mensajes Recibidos</h5>
            </Card.Header>
            <Card.Body>
              {loadingContactos ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" />
                </div>
              ) : contactos.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <p>No hay mensajes de contacto.</p>
                </div>
              ) : (
                <Table responsive hover>
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Nombre</th>
                      <th>Teléfono</th>
                      <th>Asunto</th>
                      <th>Mensaje</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contactos.map((contacto) => (
                      <tr key={contacto._id} style={contacto.leido ? {} : { fontWeight: 'bold', backgroundColor: '#f0f7ff' }}>
                        <td>{new Date(contacto.createdAt).toLocaleDateString()} {new Date(contacto.createdAt).toLocaleTimeString()}</td>
                        <td>
                          {contacto.nombre}<br />
                          <small className="text-muted">{contacto.email}</small>
                        </td>
                        <td>{contacto.telefono || '-'}</td>
                        <td>{contacto.asunto}</td>
                        <td style={{ maxWidth: '300px' }}>
                          <div style={{ maxHeight: '60px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {contacto.mensaje}
                          </div>
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            {!contacto.leido && (
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleMarkAsRead(contacto._id)}
                                title="Marcar como leído"
                              >
                                <FaCheckCircle />
                              </Button>
                            )}
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => handleDeleteContacto(contacto._id)}
                              title="Eliminar mensaje"
                            >
                              <FaTrash />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        )}

        {/* Trabajos Section (Only show if activeTab is trabajos) */}
        {activeTab === 'trabajos' && (
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white border-0 py-3">
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0" style={{ color: '#1E5096' }}>
                  <FaImage className="me-2" />
                  Gestión de Trabajos
                </h5>
                <Button variant="primary" onClick={openCreateModal} style={{ backgroundColor: '#1E5096', borderColor: '#1E5096' }}>
                  <FaPlus className="me-2" />
                  Nuevo Trabajo
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
              {loadingTrabajos ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" />
                </div>
              ) : trabajos.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <FaImage size={48} className="mb-3" />
                  <p>No hay trabajos registrados</p>
                  <Button variant="outline-primary" onClick={openCreateModal}>
                    Crear primer trabajo
                  </Button>
                </div>
              ) : (
                <Table responsive hover>
                  <thead>
                    <tr>
                      <th>Imagen</th>
                      <th>Nombre</th>
                      <th>Cliente</th>
                      <th>Categoría</th>
                      <th>Fecha</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trabajos.map((trabajo) => (
                      <tr key={trabajo._id}>
                        <td>
                          {trabajo.imagen ? (
                            <img
                              src={trabajo.imagen.startsWith('http') ? trabajo.imagen : `${API_URL}/${trabajo.imagen}`}
                              alt={trabajo.nombre}
                              style={{ width: '60px', height: '40px', objectFit: 'cover', borderRadius: '4px' }}
                            />
                          ) : (
                            <div style={{ width: '60px', height: '40px', backgroundColor: '#e9ecef', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <FaImage color="#adb5bd" />
                            </div>
                          )}
                        </td>
                        <td>
                          <strong>{trabajo.nombre}</strong>
                          <br />
                          <small className="text-muted">{trabajo.titulo}</small>
                        </td>
                        <td>{trabajo.cliente || '-'}</td>
                        <td>
                          <Badge bg="primary" style={{ backgroundColor: '#1E5096' }}>
                            {trabajo.categoria}
                          </Badge>
                        </td>
                        <td>
                          {trabajo.fecha ? new Date(trabajo.fecha).toLocaleDateString('es-UY') : '-'}
                        </td>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => openEditModal(trabajo)}
                              style={{ backgroundColor: '#1E5096', borderColor: '#1E5096' }}
                            >
                              <FaEdit />
                            </Button>
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => handleDelete(trabajo._id)}
                              style={{ backgroundColor: '#dc3545', borderColor: '#dc3545' }}
                            >
                              <FaTrash />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        )}
      </Container>

      {/* Modal para crear/editar trabajo */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        {/* ... Modal Content remains the same ... */}
        <Modal.Header closeButton style={{ borderBottom: 'none' }}>
          <Modal.Title style={{ color: '#1E5096' }}>
            {editingTrabajo ? 'Editar Trabajo' : 'Nuevo Trabajo'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Nombre del Proyecto *</Form.Label>
                  <Form.Control
                    type="text"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleInputChange}
                    placeholder="Ej: Stand Expo Prado 2024"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Título / Subtítulo</Form.Label>
                  <Form.Control
                    type="text"
                    name="titulo"
                    value={formData.titulo}
                    onChange={handleInputChange}
                    placeholder="Ej: Diseño y construcción de stand"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Cliente</Form.Label>
                  <Form.Control
                    type="text"
                    name="cliente"
                    value={formData.cliente}
                    onChange={handleInputChange}
                    placeholder="Nombre del cliente"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Categoría</Form.Label>
                  <Form.Select
                    name="categoria"
                    value={formData.categoria}
                    onChange={handleInputChange}
                  >
                    <option value="Stands">Stands</option>
                    <option value="Cartelería">Cartelería</option>
                    <option value="Exhibidores">Exhibidores</option>
                    <option value="Letras Corpóreas">Letras Corpóreas</option>
                    <option value="Mobiliario">Mobiliario</option>
                    <option value="Pantallas">Pantallas</option>
                    <option value="Impresión">Impresión</option>
                    <option value="Vinilos">Vinilos</option>
                    <option value="Señalética">Señalética</option>
                    <option value="Otros">Otros</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Ubicación</Form.Label>
                  <Form.Control
                    type="text"
                    name="ubicacion"
                    value={formData.ubicacion}
                    onChange={handleInputChange}
                    placeholder="Ej: Expo Prado, Montevideo"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Tamaño</Form.Label>
                  <Form.Control
                    type="text"
                    name="tamaño"
                    value={formData.tamaño}
                    onChange={handleInputChange}
                    placeholder="Ej: 36 m²"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Fecha</Form.Label>
                  <Form.Control
                    type="date"
                    name="fecha"
                    value={formData.fecha}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Materiales</Form.Label>
              <Form.Control
                type="text"
                name="materiales"
                value={formData.materiales}
                onChange={handleInputChange}
                placeholder="Ej: MDF, Aluminio, LED"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Descripción</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="descripcion"
                value={formData.descripcion}
                onChange={handleInputChange}
                placeholder="Descripción del proyecto..."
              />
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label>Imágenes (Máx 4, Máx 800kb c/u)</Form.Label>
              <div
                className={`p-4 border-2 rounded text-center ${dragActive ? 'border-primary bg-light' : 'border-secondary'}`}
                style={{ borderStyle: 'dashed', cursor: 'pointer', transition: 'all 0.2s' }}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => document.getElementById('file-upload').click()}
              >
                <FaImage size={32} className="mb-2 text-muted" />
                <p className="mb-0 text-muted">Arrastra tus imágenes aquí o haz clic para seleccionar</p>
                <input
                  id="file-upload"
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleFileInput}
                  style={{ display: 'none' }}
                />
              </div>

              {imagePreviews.length > 0 && (
                <div className="mt-3 d-flex gap-2 flex-wrap">
                  {imagePreviews.map((src, index) => (
                    <div key={index} className="position-relative">
                      <img
                        src={src}
                        alt={`Preview ${index}`}
                        style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '4px' }}
                      />
                      <Badge
                        bg="secondary"
                        className="position-absolute top-0 end-0"
                        style={{ cursor: 'pointer' }}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      >
                        {index + 1}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </Form.Group>

            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={() => setShowModal(false)}>
                Cancelar
              </Button>
              <Button type="submit" variant="primary" style={{ backgroundColor: '#1E5096', borderColor: '#1E5096' }}>
                {editingTrabajo ? 'Guardar Cambios' : 'Crear Trabajo'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default AdminDashboard;
